import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useContext, useRef, useState } from 'react';
import { fetchFileAccess } from '../../../api/google-integrations-client/google-integrations-client';
import { GOOGLE_FILES_QUERY_KEY } from '../../../api/google-integrations-client/google-integrations-client.hooks';
import { UserContext } from '../../../contexts/user';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { useGooglePicker } from './use-google-picker';
import { appConfig } from '../../../helpers/app-config/app-config';

/**
 * Matches Google Sheets document urls and captures the file id. The `{20,}`
 * floor rejects published `/spreadsheets/d/e/2PACX-...` links because the id
 * segment after `/d/` there is just `e` (the char class cannot cross the `/`).
 */
const SPREADSHEET_URL_REGEX = /https?:\/\/docs\.google\.com\/spreadsheets\/(?:u\/\d+\/)?d\/([a-zA-Z0-9_-]{20,})/g;

/**
 * Matches Google Docs document urls and captures the file id. Uses the same
 * `{20,}` floor as {@link SPREADSHEET_URL_REGEX} so published
 * `/document/d/e/2PACX-...` links are rejected.
 */
const DOCUMENT_URL_REGEX = /https?:\/\/docs\.google\.com\/document\/(?:u\/\d+\/)?d\/([a-zA-Z0-9_-]{20,})/g;

/**
 * Matches Google Slides presentation urls and captures the file id. Uses the
 * same `{20,}` floor as {@link SPREADSHEET_URL_REGEX} so published
 * `/presentation/d/e/2PACX-...` links are rejected.
 */
const PRESENTATION_URL_REGEX = /https?:\/\/docs\.google\.com\/presentation\/(?:u\/\d+\/)?d\/([a-zA-Z0-9_-]{20,})/g;

/** The kind of Google file a pasted link points at. */
type GoogleFileType = 'spreadsheet' | 'document' | 'presentation';

/** A Google file link extracted from chat text. */
interface GoogleFileLink {
  fileId: string;
  fileType: GoogleFileType;
}

/** How long to wait on the access check before failing open and sending. */
const FILE_ACCESS_TIMEOUT_MS = 4000;

/** Status the grant-access modal is currently in. */
type SheetAccessModalStatus =
  | 'idle'
  | 'opening-picker'
  | 'picked-different'
  | 'reconnect-required'
  | 'connect-required'
  | 'error';

/** A submission held back while the access modal is open. */
interface HeldSubmission {
  fileId: string;
  fileType: GoogleFileType;
  message?: string;
  displayMessage?: string;
}

interface UseSheetAccessGateOptions {
  /** The real Flora submit — called once access is resolved or the gate is off. */
  submitQuestion: (message?: string, displayMessage?: string) => void;
  /** The current chat input, mirrored so we can gate the fallback submit. */
  question: string;
}

interface UseSheetAccessGateResult {
  guardedSubmit: (message?: string, displayMessage?: string) => void;
  isModalOpen: boolean;
  modalStatus: SheetAccessModalStatus;
  fileType: GoogleFileType;
  grantAccess: () => Promise<void>;
  sendAnyway: () => void;
  cancel: () => void;
}

/**
 * Extract unique Google file links (Sheets, Docs, and Slides) from arbitrary
 * text, preserving the order in which they first appear and de-duplicating by
 * file id.
 *
 * @param {string} text - Text that may contain Google Sheets, Docs, or Slides urls.
 * @returns {GoogleFileLink[]} The distinct file links found, in appearance order.
 */
function extractGoogleFileLinks(text: string): GoogleFileLink[] {
  const matchers: Array<{ regex: RegExp; fileType: GoogleFileType }> = [
    { regex: SPREADSHEET_URL_REGEX, fileType: 'spreadsheet' },
    { regex: DOCUMENT_URL_REGEX, fileType: 'document' },
    { regex: PRESENTATION_URL_REGEX, fileType: 'presentation' },
  ];

  const matches: Array<{ index: number; fileId: string; fileType: GoogleFileType }> = [];
  for (const { regex, fileType } of matchers) {
    for (const match of text.matchAll(regex)) {
      const fileId = match[1];
      if (fileId && match.index !== undefined) {
        matches.push({ index: match.index, fileId, fileType });
      }
    }
  }
  matches.sort((first, second) => first.index - second.index);

  const seen = new Set<string>();
  const links: GoogleFileLink[] = [];
  for (const match of matches) {
    if (!seen.has(match.fileId)) {
      seen.add(match.fileId);
      links.push({ fileId: match.fileId, fileType: match.fileType });
    }
  }
  return links;
}

/**
 * Gate Flora submissions on Google file access: when a pasted Google Sheets,
 * Docs, or Slides link is not accessible to Flora, hold the send and drive a modal that
 * offers to grant access through the Google Picker. The chat must never block
 * on this feature, so any error or timeout fails open and sends the message.
 *
 * @param {UseSheetAccessGateOptions} options - The real submit and current input.
 * @returns {UseSheetAccessGateResult} A guarded submit and modal controls.
 */
function useSheetAccessGate({ submitQuestion, question }: UseSheetAccessGateOptions): UseSheetAccessGateResult {
  const { user } = useContext(UserContext);
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const apiKey = appConfig.VITE_APP_GOOGLE_API_KEY;
  const { openPickerForFile } = useGooglePicker();
  const queryClient = useQueryClient();

  // The gate is active whenever the Google feature is usable, regardless of
  // connection state: an unconnected user who pastes a sheet link still needs
  // to be prompted to connect (rather than silently sending).
  const isGateActive = Boolean(apiKey && organizationId);
  const isConnected = Boolean(user?.has_google_creds);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalStatus, setModalStatus] = useState<SheetAccessModalStatus>('idle');
  // Drives the modal copy; mirrors the held submission's file type so the state
  // is available to the render without reading the ref.
  const [fileType, setFileType] = useState<GoogleFileType>('spreadsheet');
  const heldSubmissionRef = useRef<HeldSubmission | null>(null);
  // Session cache: 'accessible' once verified, 'send-anyway' once overridden.
  const accessCacheRef = useRef<Map<string, 'accessible' | 'send-anyway'>>(new Map());

  const submitHeld = useCallback(() => {
    const held = heldSubmissionRef.current;
    heldSubmissionRef.current = null;
    setIsModalOpen(false);
    setModalStatus('idle');
    if (held) {
      submitQuestion(held.message, held.displayMessage);
    }
  }, [submitQuestion]);

  const guardedSubmit = useCallback(
    (message?: string, displayMessage?: string) => {
      if (!isGateActive || !organizationId) {
        submitQuestion(message, displayMessage);
        return;
      }

      const messageToCheck = message || question;
      const [firstLink] = extractGoogleFileLinks(messageToCheck);
      if (!firstLink) {
        submitQuestion(message, displayMessage);
        return;
      }
      const { fileId, fileType: linkFileType } = firstLink;

      // A file link is present but Google isn't connected: hold the send and
      // prompt the user to connect their account before Flora can use it.
      if (!isConnected) {
        heldSubmissionRef.current = { fileId, fileType: linkFileType, message, displayMessage };
        setFileType(linkFileType);
        setModalStatus('connect-required');
        setIsModalOpen(true);
        return;
      }

      if (accessCacheRef.current.has(fileId)) {
        submitQuestion(message, displayMessage);
        return;
      }

      void (async () => {
        let timeoutId: ReturnType<typeof setTimeout> | undefined;
        try {
          const accessCheck = fetchFileAccess(organizationId, fileId);
          const timeout = new Promise<never>((_resolve, reject) => {
            timeoutId = setTimeout(() => reject(new Error('file access check timed out')), FILE_ACCESS_TIMEOUT_MS);
          });
          const accessible = await Promise.race([accessCheck, timeout]);

          if (accessible) {
            accessCacheRef.current.set(fileId, 'accessible');
            submitQuestion(message, displayMessage);
            return;
          }

          heldSubmissionRef.current = { fileId, fileType: linkFileType, message, displayMessage };
          setFileType(linkFileType);
          setModalStatus('idle');
          setIsModalOpen(true);
        } catch {
          // Fail open — chat must never block on the access check.
          submitQuestion(message, displayMessage);
        } finally {
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
        }
      })();
    },
    [isGateActive, isConnected, organizationId, question, submitQuestion],
  );

  const grantAccess = useCallback(async () => {
    const held = heldSubmissionRef.current;
    if (!held || !organizationId) {
      return;
    }

    setModalStatus('opening-picker');

    const outcome = await openPickerForFile(organizationId, held.fileId, held.fileType);
    switch (outcome) {
      case 'granted':
        accessCacheRef.current.set(held.fileId, 'accessible');
        // The newly granted file is now drive.file-accessible, so the Documents
        // page's Google Drive listing is stale — drop it so it refetches.
        queryClient.invalidateQueries({ queryKey: [GOOGLE_FILES_QUERY_KEY] });
        submitHeld();
        break;
      case 'reconnect-required':
        setModalStatus('reconnect-required');
        break;
      case 'picked-different':
        setModalStatus('picked-different');
        break;
      case 'error':
        setModalStatus('error');
        break;
      case 'cancelled':
        setModalStatus('idle');
        break;
    }
  }, [organizationId, submitHeld, openPickerForFile, queryClient]);

  const sendAnyway = useCallback(() => {
    const held = heldSubmissionRef.current;
    if (held) {
      accessCacheRef.current.set(held.fileId, 'send-anyway');
    }
    submitHeld();
  }, [submitHeld]);

  const cancel = useCallback(() => {
    heldSubmissionRef.current = null;
    setIsModalOpen(false);
    setModalStatus('idle');
  }, []);

  return { guardedSubmit, isModalOpen, modalStatus, fileType, grantAccess, sendAnyway, cancel };
}

export { extractGoogleFileLinks, useSheetAccessGate };
export type { GoogleFileLink, GoogleFileType, SheetAccessModalStatus };
